export const esSwapUi = {
  connectWallet: 'Conectar wallet',
  convertToWeth: 'Convertir a WETH',
  convertToEth: 'Convertir a ETH',
  convert: 'Convertir',
  approved: 'Aprobado',
  approveToken: (symbol: string) => `Aprobar ${symbol}`,
  awaitingWalletSignature: 'Esperando firma en wallet',
  awaitingConfirmation: 'Esperando confirmación',
  cancelWalletAction: 'Cancelar operación',
  cancelWalletActionHint:
    'Si cerraste MetaMask sin firmar ni rechazar, cancela aquí para desbloquear la pantalla. Podrás volver a intentar la operación cuando quieras.',
  awaitingConfirmationHint:
    'La transacción ya fue enviada a la red. Solo queda esperar la confirmación on-chain.',
  impactVeryHigh: 'Impacto muy alto',
  impactTooHigh: 'Impacto demasiado alto',
  swapAnyway: 'Intercambiar igualmente',
  swap: 'Intercambiar',
  selectTokenTitle: (field: string) => `Seleccionar token — ${field}`,
  field: { pay: 'Pagar', receive: 'Recibir' },
  selectPairForQuote: 'Selecciona un par para ver la cotización de mercado.',
  marketPrice: 'Precio de mercado',
  rate: 'Tasa',
  minReceived: 'Mín. recibido',
  maxSold: 'Máx. vendido',
  invalidPair: 'Par inválido.',
  priceImpact: 'Impacto de precio',
  impact: 'Impacto',
  priceImpactGuide: {
    safe: '«No entres en pánico»',
    caution: '«Mayormente inofensivo»',
    blocked: '«Más allá de lo inofensivo»',
    limitPair: (pct: string) => `Límite del par: ${pct}%`,
    limitGlobal: (pct: string) => `Límite global: ${pct}%`,
  },
  slippage: 'Slippage',
  confirmReceiveNotice: {
    title: 'Aviso sobre la cantidad a recibir',
    body:
      'La cantidad estimada a recibir puede ser menor o cambiar antes de que se confirme la transacción, sobre todo en periodos de alta volatilidad o cuando hay mucha actividad en el par.',
    slippageHint:
      'Si tus operaciones se revierten, considera aumentar la tolerancia de slippage en',
    settingsLink: 'Ajustes de intercambio',
  },
  enterAmountIdle:
    'Introduce un monto para ver tasa, mínimo recibido e impacto del intercambio.',
  enterAmountIdleShort: 'Introduce un monto para ver tasa, mínimo e impacto.',
  estimatedLpFee: 'Comisión LP estimada:',
  searchPairPlaceholder: 'Buscar par…',
  noPairsOnChain: 'No hay pares en el catálogo para esta cadena.',
  noPairSearchMatch: 'Ningún par coincide con tu búsqueda.',
  label: {
    from: 'From',
    to: 'To',
    fromEstimated: 'From (estimated)',
    toEstimated: 'To (estimated)',
  },
  error: {
    insufficientLiquidity: 'Liquidez insuficiente para este intercambio.',
    swapDisabled: 'Swapping this token is not available',
    prepareTxFailed: 'No se pudo preparar la transacción. Abre MetaMask e inténtalo de nuevo.',
    revertedOnChain: 'Transaction reverted on-chain.',
  },
  synthesis: {
    connectToDispense: 'Conectar wallet para dispensar',
    wrapToWeth: 'Conversión a WETH',
    unwrapToEth: 'Conversión a ETH',
    wrapAvailable: 'Conversión disponible',
    approvalPending: 'Aprobación enviada — esperando confirmación',
    approveToContinue: (symbol: string) => `Aprobar ${symbol} para continuar`,
    impactTooHighDispense: 'Impacto demasiado alto para dispensar',
    enterValidAmounts: 'Introduce montos válidos',
    highImpactReview: 'Impacto elevado — revisar telemetría del display',
    invalidAmountsForPair: 'Montos no válidos para este par',
    enterAmountsToDispense: 'Introduce montos para dispensar',
    approvalPendingToken: 'Aprobación de token pendiente',
  },
  readout: {
    syncingQuote: 'Sincronizando la cotización del mercado…',
    selectPairInBay: 'Selecciona el par de tokens en la bahía para comenzar.',
    noPoolAvailable: 'Este par no tiene pool disponible. Elige otro par.',
    reviewBeforeConfirm: 'Parámetros estimados de tu swap — revísalos antes de confirmar.',
    enterAmount: 'Indica la cantidad que deseas intercambiar.',
  },
  tokenDialog: { pay: 'Pagar', receive: 'Recibir' },
  entry: {
    poolNotDeployed:
      'Esta pool aún no está desplegada on-chain. Puedes crear liquidez para este par.',
    addLiquidityCta: 'Añadir liquidez',
    dismiss: 'Entendido',
  },
} as const

export const esPoolUi = {
  liquidityPool: 'Pool de liquidez',
  vol24h: 'Vol 24h',
  tradeOnNutrimatic: 'Operar en Nutrimatic',
  reservesInPool: 'Reservas en pool',
  composition: 'Composición del pool',
  compositionAria: (token0: string, share0: string, token1: string, share1: string) =>
    `Composición ${token0} ${share0}, ${token1} ${share1}`,
  compositionCaptionIndexed: 'Datos del catálogo de mercado (indexer).',
  compositionCaptionOnChain: 'Estimado on-chain para un par fuera del catálogo indexado.',
  onChainContracts: 'Contratos on-chain',
  lpSharesFootnote:
    'En pools V2, el contrato del par emite las LP shares al aportar liquidez. No hay un token LP separado.',
  metric: {
    price: 'Precio',
    priceSub: 'spot',
    change24h: 'Cambio 24h',
    change24hSub: 'movimiento de precio',
    tvl: 'TVL',
    tvlSub: 'valor total bloqueado',
    volume24hSub: 'operaciones spot',
    reserve0: 'Reserva 0',
    reserve1: 'Reserva 1',
    feeSub: 'por swap',
    apyEstimated: 'APY estimado',
    apySub: 'fees + incentivos',
    volume7d: 'Volumen 7d',
    volume7dSub: 'acumulado',
    trades24h: 'Operaciones 24h',
    trades24hSub: 'swaps en pool',
    tvlComposition: 'Composición TVL',
  },
  createdAt: (date: string) => `creada ${date}`,
  noPairPriceData: 'Sin datos de precio para este par.',
  chartTitle24h: 'Precio del par (24h)',
  poolsOfToken: (symbol: string) => `Pools de ${symbol}`,
  ecosystemLiquidityTitle: 'Liquidez del ecosistema',
  addLiquidityCta: 'Añadir liquidez',
} as const

export const esTokenDetail = {
  invalidAddress: 'Dirección de contrato no válida.',
  profileUnavailable: 'Perfil de token no disponible.',
  tradeOnNutrimatic: 'Comerciar en Nutrimatic',
  noPriceData: 'Sin datos de precio para este token.',
  liquidity: {
    noPools: (symbol: string) => `No hay pools asociadas a ${symbol}.`,
    viewEcosystemPools: 'Ver pools del ecosistema',
    poolsWithSymbol: (symbol: string) => `Pools con ${symbol}`,
    shareInPool: (tvl: string, share: string) => `TVL ${tvl} · Share ${share}`,
    exploreEcosystem: 'Explorar todo el ecosistema',
  },
} as const

export const esBridge = {
  title: 'Bridge',
  subtitle: 'Transferencias entre cadenas',
  description:
    'Mueve activos entre Infinite Drive y otras redes compatibles usando el puente oficial.',
  chainRoute: 'Cadena origen → Cadena destino',
  openBridgeSite: 'Abrir puente',
  backToSwap: 'Volver al Swap',
} as const

export const esProfile = {
  title: 'Profile',
  subtitle: 'Identidad y cartera. Historial completo y overview en Portfolio.',
  wallet: { title: 'Wallet', description: 'Detalles de cuenta conectada' },
  disconnect: 'Disconnect',
  resetHint: '¿No responde el botón?',
  resetConnection: 'Restablecer conexión de wallet',
  achievements: 'Achievements',
  achievement: {
    towelHolder: 'Towel holder',
    firstSwap: 'First swap',
    poolPioneer: 'Pool pioneer',
    bridgeCurious: 'Bridge curious',
  },
  connectPrompt: 'Conecta tu wallet para ver tu perfil.',
  connectWallet: 'Connect Wallet',
} as const

export const esTradeSettings = {
  transactionTitle: 'Ajustes de transacción',
  interfaceTitle: 'Ajustes de interfaz',
  slippage: {
    label: 'Tolerancia de slippage',
    helper:
      'Tu transacción se revertirá si el precio cambia desfavorablemente más de este porcentaje.',
    description:
      'Margen máximo que aceptas entre la cotización mostrada y el precio al confirmar on-chain. Si el mercado se mueve más que este valor, la transacción se revierte.',
    help: {
      introTitle: '¿Qué es el slippage?',
      cautionTitle: 'Antes de subir el valor',
      caution:
        'Un margen muy amplio acepta un precio final peor que la cotización mostrada: podrías recibir menos de lo que esperabas. Resérvalo para mercados muy volátiles.',
    },
    presetGuide: {
      title: 'Guía rápida por escenario',
      p01: 'Pares muy estables (p. ej. stablecoin ↔ stablecoin)',
      p05: 'Uso habitual en la mayoría de swaps',
      p1: 'Mercado activo o tokens con spread amplio',
      p25: 'Alta volatilidad, lanzamientos o pools muy concurridos',
      p5: 'Tope habitual en este DEX; solo en situaciones extremas',
      custom: 'También puedes escribir cualquier otro valor en el campo personalizado.',
    },
    invalid: 'Introduce un porcentaje de slippage válido',
    customLabel: 'Personalizado',
    riskyLow: 'Slippage muy bajo: la transacción puede revertirse si el precio se mueve un poco',
    riskyHigh:
      'Por encima del 5%: el precio final puede alejarse de la cotización y el resultado ser menos favorable de lo que aceptaste.',
  },
  deadline: {
    label: 'Plazo límite de transacción',
    helper: 'Tu transacción se revertirá si permanece pendiente más tiempo del indicado.',
    description:
      'Tiempo máximo (en minutos) que la transacción puede quedar pendiente en la mempool antes de expirar.',
    help: {
      introTitle: '¿Qué es el plazo límite?',
    },
    examples:
      '20–30 minutos suele ser suficiente. Valores muy bajos (p. ej. 5 min) pueden hacer fallar el swap si la red está congestionada o tardas en firmar.',
    minutes: 'minutos',
  },
  expertMode: {
    confirmTitle: '¿Estás seguro?',
    warning:
      'El modo experto desactiva la confirmación de transacción y permite trades con slippage alto que suelen resultar en malas tasas y fondos perdidos.',
    onlyIfYouKnow: 'USA ESTE MODO SOLO SI SABES LO QUE HACES.',
    turnOn: 'Activar modo experto',
    promptConfirm: 'Escribe la palabra "confirm" para activar el modo experto.',
    toggle: 'Alternar modo experto',
    toggleHelper:
      'Omite modales de confirmación y permite trades con slippage alto. Úsalo bajo tu responsabilidad.',
  },
} as const
